import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, X, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AnimatePresence, motion } from "framer-motion";
import BotSweepLogo from "./BotSweepLogo";
import { useAuth } from "@/contexts/AuthContext";

const navLinks = [
  { href: "#problem", label: "Why BotSweep" },
  { href: "#pricing", label: "Pricing" },
  { href: "#how-it-works", label: "How It Works" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogoClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <>
      {/* Announcement Bar */}
      <a
        href="#problem"
        onClick={(e) => { e.preventDefault(); document.querySelector('#ai-traffic-section')?.scrollIntoView({ behavior: 'smooth' }); }}
        className="fixed top-0 left-0 right-0 z-[60] bg-primary/5 border-b border-primary/10 backdrop-blur-sm cursor-pointer hover:bg-primary/10 transition-colors"
      >
        <div className="max-w-7xl mx-auto px-6 h-8 flex items-center justify-center gap-2">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse-glow" />
          <span className="text-[10px] font-mono text-primary/80 tracking-wide">
            Now detecting AI-generated bot traffic
          </span>
          <ArrowRight className="w-3 h-3 text-primary/60" />
        </div>
      </a>

      {/* Nav */}
      <nav
        className={`fixed top-8 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-background/60 backdrop-blur-2xl border-b border-border/50 shadow-[0_4px_30px_hsl(191_100%_50%/0.05)]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <a href="/" onClick={handleLogoClick} className="group flex items-center gap-2 cursor-pointer">
            <BotSweepLogo size={26} />
            <span className="font-display font-bold text-lg tracking-tight text-foreground">
              BotSweep<span className="text-primary">.</span>
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="text-xs font-mono text-muted-foreground hover:text-foreground transition-colors uppercase tracking-[0.15em]"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              <Link to="/dashboard">
                <Button size="sm" className="text-[11px] font-mono bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-5 uppercase tracking-[0.1em]">
                  <LayoutDashboard className="w-3.5 h-3.5 mr-1.5" />
                  Dashboard
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/auth" className="text-[11px] font-mono text-muted-foreground hover:text-foreground transition-colors uppercase tracking-[0.15em]">
                  Log in
                </Link>
                <Link to="/auth">
                  <Button size="sm" className="text-[11px] font-mono bg-primary text-primary-foreground hover:bg-primary/90 h-8 px-5 uppercase tracking-[0.1em]">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(true)}
            className="md:hidden relative z-[70] p-2 -mr-2"
            aria-label="Open menu"
          >
            <div className="flex flex-col gap-[5px]">
              <div className="w-5 h-[1.5px] bg-foreground transition-all" />
              <div className="w-3.5 h-[1.5px] bg-foreground transition-all" />
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile slide-out menu */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm"
              onClick={() => setMobileOpen(false)}
            />

            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed top-0 right-0 bottom-0 z-[90] w-[280px] bg-background/95 backdrop-blur-2xl border-l border-border/30 flex flex-col"
            >
              <div className="flex items-center justify-between px-6 h-[calc(2rem+3.5rem)]">
                <a href="/" onClick={(e) => { handleLogoClick(e); setMobileOpen(false); }} className="flex items-center gap-2 cursor-pointer">
                   <BotSweepLogo size={18} />
                   <span className="font-display font-bold text-sm text-foreground">
                     BotSweep<span className="text-primary">.</span>
                   </span>
                 </a>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-2 -mr-2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Close menu"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 px-6 pt-4">
                <div className="space-y-1">
                  {navLinks.map((link, i) => (
                    <motion.a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => {
                        e.preventDefault();
                        setMobileOpen(false);
                        setTimeout(() => {
                          document.querySelector(link.href)?.scrollIntoView({ behavior: 'smooth' });
                        }, 350);
                      }}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 + i * 0.05 }}
                      className="block py-3 px-3 text-sm font-mono text-muted-foreground hover:text-foreground hover:bg-primary/5 rounded-lg transition-colors uppercase tracking-[0.15em]"
                    >
                      {link.label}
                    </motion.a>
                  ))}
                </div>

                <div className="my-6 h-px bg-border/30" />

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="mt-4"
                >
                  {user ? (
                    <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                      <Button className="w-full text-xs font-mono bg-primary text-primary-foreground hover:bg-primary/90 h-10 uppercase tracking-[0.1em]">
                        <LayoutDashboard className="w-3.5 h-3.5 mr-2" />
                        Dashboard
                      </Button>
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/auth"
                        className="block w-full text-left py-3 px-3 text-sm font-mono text-muted-foreground hover:text-foreground hover:bg-primary/5 rounded-lg transition-colors uppercase tracking-[0.15em]"
                        onClick={() => setMobileOpen(false)}
                      >
                        Log in
                      </Link>
                      <Link to="/auth" onClick={() => setMobileOpen(false)} className="block mt-4">
                        <Button className="w-full text-xs font-mono bg-primary text-primary-foreground hover:bg-primary/90 h-10 uppercase tracking-[0.1em] btn-shimmer">
                          Get Started
                          <ArrowRight className="w-3.5 h-3.5 ml-2" />
                        </Button>
                      </Link>
                    </>
                  )}
                </motion.div>
              </div>

              <div className="px-6 pb-8">
                <p className="text-[9px] font-mono text-muted-foreground/40">
                  © 2026 BotSweep. All rights reserved.
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
